import React, { useContext } from "react";
import {
  Divider,
  Text,
  Button,
  TextLinkStyles,
} from "@origin-digital/ods-core";
import styled from "styled-components";
import { RecommendedSearchResult, SearchResult } from "./SearchResult";
import { SearchTabs } from "./SearchTabs";
import { ISearchResponse, ISearchResult } from "../../helpers/search";
import { RelatedSearches } from "./RelatedSearches";
import { NoResults, SpellingSuggestion } from "./NoResults";
import {
  getSquizSearchDefaultOptions,
  squizCreateQueryString,
} from "../../helpers/squiz";
import { AppContext } from "../../AppContext";

const SAFE_HOSTNAMES = ["www.originenergy.com.au", "www.youtube.com"];

const ResultItemsContainer = styled("div")`
  display: grid;
  gap: ${(props) => props.theme.space.xlarge}px;
  grid-area: results;
`;

const Summary = styled("div")`
  grid-area: summary;
`;

const SearchTabsContainer = styled("div")`
  grid-area: facets;
  overflow-x: auto;
`;

const ResultsContainer = styled("div")`
  display: grid;
  row-gap: ${(props) => props.theme.space.xlarge}px;

  grid-template-areas:
    "facets"
    "summary"
    "results";

  ${({ theme }) => theme.breakpoints.up("lg")} {
    column-gap: 72px;

    grid-template-columns: unset;
    grid-template-areas:
      "facets summary"
      "facets results";
  }
`;

const PaginationButton = styled(Button)`
  &.MuiButton-root {
    width: 40px;
    height: 40px;
    min-width: 0;
    padding: 0;
    border-radius: 50%;
  }
`;

const PaginationWrapper = styled("div")`
  display: grid;
  grid-auto-flow: column;
  width: fit-content;
  gap: 12px;
`;

type ArticleCategory =
  | "Help & Support"
  | "Blog"
  | "Investors Media"
  | undefined;
const getArticleCategory = (url: string): ArticleCategory => {
  if (url.includes("/help-support/")) return "Help & Support";
  if (url.includes("/blog/")) return "Blog";
  if (url.includes("/investors-media/")) return "Investors Media";
  return undefined;
};

export type SearchResult =
  // Searching also alows data, this way we can keep the previous screen up
  // while the new one is loading
  | { type: "Searching"; data?: ISearchResponse }
  | { type: "Success"; data: ISearchResponse }
  | { type: "Error" };

const displayTitle = (result: ISearchResult) =>
  `${result.title} ${result.url.includes(".pdf") ? "(PDF)" : ""}`;

export const SearchResults = ({
  handleSearch,
  searchResults,
}: {
  searchResults: SearchResult;
  handleSearch: (str: string) => Promise<void>;
}) => {
  const { config } = useContext(AppContext);

  switch (searchResults.type) {
    case "Searching":
    case "Success": {
      if (searchResults.data == null) return <></>;

      const {
        resultsSummary: summary,
        question,
        facets,
        pagination,
        results: unfilteredResults,
        relatedSearches: unfilteredRelatedSearches,
        spellingSuggestion,
        substituteQuery,
      } = searchResults.data;

      // SECURITY: only allow urls that point a user to sites we trust
      const results = unfilteredResults.filter((res) =>
        SAFE_HOSTNAMES.includes(new URL(res.url).hostname)
      );

      if (results.length === 0)
        return <NoResults spellingSuggestion={spellingSuggestion?.text} />;

      const relatedSearches = unfilteredRelatedSearches.filter(
        (item) => item.query != null
      );
      const tabs = facets.find((facet) => facet.name === "Tabs");
      const isPageOne = !pagination?.previous;

      return (
        <ResultsContainer>
          <SearchTabsContainer>
            <SearchTabs tabs={tabs?.values} handleSearch={handleSearch} />
          </SearchTabsContainer>
          <Summary>
            <Text data-id="total-count">
              Results {summary?.currStart}-{summary?.currEnd} of{" "}
              {summary?.totalMatching} for{" "}
              <Text tone="neutralDark" weight="medium" inline>
                {substituteQuery ? substituteQuery : question!.query}
              </Text>
            </Text>
            {spellingSuggestion && (
              <SpellingSuggestion
                spellingSuggestion={spellingSuggestion.text}
              />
            )}
            {substituteQuery && (
              <Text>
                Show results for{" "}
                <Text inline tone="neutralDark" weight="medium">
                  <TextLinkStyles
                    as="button"
                    onClick={(e: Event) => {
                      e.preventDefault();
                      handleSearch(
                        `?${squizCreateQueryString({
                          ...getSquizSearchDefaultOptions(config.env),
                          query: encodeURIComponent(question.query),
                        })}&qsup=off`
                      );
                    }}
                    href={`?term=${encodeURIComponent(question.query)}`}
                  >
                    {question.query}
                  </TextLinkStyles>
                </Text>
              </Text>
            )}
          </Summary>
          <ResultItemsContainer>
            {isPageOne && (
              <RecommendedSearchResult
                href={results[0].clickTrackingUrl}
                subtitle={results[0].summary}
                title={displayTitle(results[0])}
                category={getArticleCategory(results[0].url)}
              />
            )}
            {(isPageOne ? results.slice(1) : results).map((result, idx) => {
              return (
                <React.Fragment key={result.url}>
                  {idx !== 0 && <Divider />}
                  <SearchResult
                    href={result.clickTrackingUrl}
                    subtitle={result.summary}
                    title={displayTitle(result)}
                    category={getArticleCategory(result.url)}
                  />
                </React.Fragment>
              );
            })}
            {relatedSearches.length > 0 && isPageOne && (
              <RelatedSearches
                // only return the first 3 suggestions
                relatedItems={relatedSearches.slice(0, 3)}
              />
            )}
            <PaginationWrapper>
              {pagination?.pages.map((page) => (
                <PaginationButton
                  key={page.label}
                  variant={page.selected ? "contained" : "text"}
                  color="secondaryB"
                  size="small"
                  onClick={async () => {
                    await handleSearch(page.url);
                    window.scrollTo({ top: 0, behavior: "instant" });
                  }}
                >
                  {page.label}
                </PaginationButton>
              ))}
            </PaginationWrapper>
          </ResultItemsContainer>
        </ResultsContainer>
      );
    }

    case "Error":
    default: {
      return <p>Something went wrong, Please try again later</p>;
    }
  }
};
